import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, ShieldCheck, Leaf, BadgeCheck, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CTAButtons, CTABand, Eyebrow, Marquee, Reveal, ArrowCard, ICONS } from "@/components/chrome";
import { SITE } from "@/data/site";
import { SERVICES } from "@/data/services";
import { PROVINCES, CITY_COUNT } from "@/data/provinces";
import { SECTORS } from "@/data/sectors";
import { INSIGHTS } from "@/data/insights";
import { paths } from "@/lib/seo";
import { ReviewsWall } from "@/components/Reviews";

const HERO_IMG = "https://images.unsplash.com/photo-1613575831056-0acd5da8f085?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";
const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }}
        transition={{ duration: 0.9, delay, ease: EASE }}>
        {children}
      </motion.span>
    </span>
  );
}

function Finder() {
  const navigate = useNavigate();
  const [p, setP] = useState("ontario");
  const [c, setC] = useState("toronto");
  const [s, setS] = useState("house-cleaning");
  const province = PROVINCES.find((x) => x.slug === p);
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)] sm:p-8" data-testid="service-finder">
      <Eyebrow>Find your local crew</Eyebrow>
      <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
        {CITY_COUNT} cities. 12 services. One call.
      </h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <Select value={p} onValueChange={(v) => { setP(v); setC(PROVINCES.find((x) => x.slug === v).cities[0].slug); }}>
          <SelectTrigger data-testid="finder-province-select" className="h-12"><SelectValue placeholder="Province" /></SelectTrigger>
          <SelectContent>
            {PROVINCES.map((x) => <SelectItem key={x.slug} value={x.slug} data-testid={`finder-province-${x.slug}`}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={c} onValueChange={setC}>
          <SelectTrigger data-testid="finder-city-select" className="h-12"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            {province.cities.map((x) => <SelectItem key={x.slug} value={x.slug} data-testid={`finder-city-${x.slug}`}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={s} onValueChange={setS}>
          <SelectTrigger data-testid="finder-service-select" className="h-12"><SelectValue placeholder="Service" /></SelectTrigger>
          <SelectContent>
            {SERVICES.map((x) => <SelectItem key={x.slug} value={x.slug} data-testid={`finder-service-${x.slug}`}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button data-testid="finder-go-button" onClick={() => navigate(paths.serviceCity(p, c, s))}
        className="mt-5 h-12 w-full rounded-full bg-cyan-600 text-sm font-semibold text-white transition-colors hover:bg-cyan-700 sm:w-auto sm:px-8">
        View {SERVICES.find((x) => x.slug === s).short} in {province.cities.find((x) => x.slug === c)?.name}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

const CHAPTERS = [
  { n: "01", t: "Pristine by default", d: "Every visit runs on our 50-point checklist and ends with a documented quality check. Supervisors audit monthly. If it's not right, we return and make it right — free." },
  { n: "02", t: "Local crews, national reach", d: `Vetted, insured, background-checked professionals in ${CITY_COUNT}+ Canadian communities — never subcontracted, never anonymous. The same faces, every visit.` },
  { n: "03", t: "Flat-rate honesty", d: "A firm quote before anyone picks up a mop. No hourly meters running, no surprise upsells on arrival. Most quotes arrive within one business hour." },
  { n: "04", t: "Green where it counts", d: "Eco-certified products, microfibre low-waste systems, and HEPA filtration as standard — safe for kids, pets, and sensitive workplaces." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="dot-grid pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full bg-cyan-100/60 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-16 sm:pt-24 lg:grid-cols-12 lg:pb-28">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-800" data-testid="hero-badge">
                <MapPin className="h-3.5 w-3.5" /> 13 provinces & territories — coast to coast
              </span>
            </motion.div>
            <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl" data-testid="hero-title">
              <MaskedLine delay={0.15}>Spotless spaces.</MaskedLine>
              <MaskedLine delay={0.3}>Every province.</MaskedLine>
              <MaskedLine delay={0.45}><span className="text-cyan-600">Every city.</span></MaskedLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.7 }}
              className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="hero-subtitle">
              Tbay Cleaning Services is Canada's professional cleaning network — house cleaning, commercial cleaning,
              deep cleaning and nine more specialized services, delivered by insured local crews and booked
              with a single phone call. No forms. No waiting.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}>
              <CTAButtons className="mt-9" prefix="hero" />
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500" data-testid="hero-trust-chips">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-600" /> Insured & bonded</span>
              <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-cyan-600" /> Background-checked crews</span>
              <span className="flex items-center gap-2"><Leaf className="h-4 w-4 text-cyan-600" /> Eco-certified products</span>
            </motion.div>
          </div>
          <div className="relative lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 0.9, ease: EASE }}
              className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-[2rem] border border-cyan-200" />
              <div className="relative overflow-hidden rounded-[2rem]">
                <motion.img src={HERO_IMG} alt="Pristine professionally cleaned bright living space in Canada"
                  className="aspect-[4/5] w-full object-cover" style={{ y: imgY, scale: 1.15 }} data-testid="hero-image" />
              </div>
              <div className="absolute -bottom-6 -left-6 rounded-2xl border border-white/40 bg-white/80 px-6 py-5 shadow-xl backdrop-blur-xl" data-testid="hero-stat-card">
                <p className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{CITY_COUNT}+</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-widest text-slate-500">Canadian cities served</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={[...SERVICES.map((s) => s.short), "Coast to Coast", "Insured & Bonded"]} />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-20" data-testid="stats-band">
        <div className="grid grid-cols-2 gap-y-10 lg:grid-cols-4">
          {[["13", "Provinces & territories"], [`${CITY_COUNT}+`, "Cities served"], ["12", "Specialized services"], ["1", "Phone call to book"]].map(([n, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <p className="font-display text-5xl font-extrabold tracking-tight text-slate-900 sm:text-6xl">{n}</p>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-20" data-testid="finder-section">
        <Reveal><Finder /></Reveal>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="manifesto">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:py-28">
          <Reveal>
            <Eyebrow>The Tbay standard</Eyebrow>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              A cleaning company built like a promise.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-x-14 gap-y-12 md:grid-cols-2">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.n} delay={i * 0.08} className="border-t border-slate-200 pt-6">
                <p className="font-mono text-sm font-semibold text-cyan-700">{ch.n}</p>
                <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">{ch.t}</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{ch.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:py-28" data-testid="services-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>What we clean</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">12 specialized services</h2>
          </div>
          <Link to="/services" data-testid="services-view-all-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 transition-colors hover:text-cyan-800">
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <ArrowCard to={paths.service(s.slug)} icon={s.icon} title={s.name} desc={s.tagline}
                testid={`service-card-${s.slug}`} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="coverage-section">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:py-28">
          <Reveal>
            <Eyebrow>Where we clean</Eyebrow>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Canadian-owned. Coast to coast to coast.
            </h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-slate-600">
              Tbay Cleaning Services operates vetted local crews in every Canadian province and territory.
              Select yours.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {PROVINCES.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 4) * 0.05}>
                <Link to={paths.province(p.slug)} data-testid={`province-card-${p.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-[#FAFAFC] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40 hover:shadow-[0_16px_40px_-16px_rgba(2,132,199,0.25)]">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">{p.code}</span>
                    <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-cyan-600" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">{p.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-500">
                    {p.cities.length} {p.cities.length === 1 ? "city" : "cities"} — {p.cities.slice(0, 3).map((c) => c.name).join(", ")}{p.cities.length > 3 ? "…" : ""}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:py-28" data-testid="sectors-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Sectors we serve</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">Every industry, spotless</h2>
          </div>
          <Link to="/sectors" data-testid="sectors-view-all-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 transition-colors hover:text-cyan-800">
            All sectors <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SECTORS.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.slug} delay={(i % 4) * 0.05}>
                <Link to={paths.sector(s.slug)} data-testid={`sector-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
                  <Icon className="h-6 w-6 text-cyan-700" />
                  <h3 className="mt-4 font-display text-base font-semibold tracking-tight text-slate-900">{s.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <ReviewsWall />

      <section className="border-t border-slate-200 bg-white" data-testid="insights-section">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:py-28">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Eyebrow>Insights</Eyebrow>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">Cleaning knowledge, Canadian context</h2>
            </div>
            <Link to="/insights" data-testid="insights-view-all-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 transition-colors hover:text-cyan-800">
              All articles <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {INSIGHTS.slice(0, 3).map((post, i) => (
              <Reveal key={post.slug} delay={i * 0.06}>
                <Link to={paths.insight(post.slug)} data-testid={`insight-card-${post.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-[#FAFAFC] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-cyan-700">{post.category} · {post.readMinutes} min read</p>
                  <h3 className="mt-4 flex-1 font-display text-lg font-semibold leading-snug tracking-tight text-slate-900">{post.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{post.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="One call. Spotless."
        lead={`Talk to a real coordinator today — most quotes arrive within one business hour. Serving ${CITY_COUNT}+ Canadian cities, coast to coast.`}
        prefix="home-cta"
      />
    </div>
  );
}
